(() => {
  const ensureRuntime = () => {
    const theme = window.PXUTheme ||= {};
    const routes = theme.routes ||= {};

    routes.root_url ||= '/';
    routes.cart_url ||= '/cart';
    routes.cart_add_url ||= '/cart/add.js';
    routes.search_url ||= '/search';
    routes.predictive_search_url ||= '/search/suggest';
    routes.product_recommendations_url ||= '/recommendations/products';

    theme.theme_settings ||= {};
    return theme;
  };

  ensureRuntime();

  const syncHeaderHeight = () => {
    const announcement = document.querySelector('[data-site-announcement]');
    const header = document.querySelector('[data-site-header]');

    document.body.style.setProperty('--announcement-height', `${announcement?.offsetHeight || 0}px`);
    document.body.style.setProperty('--header-height', `${header?.offsetHeight || 0}px`);
    syncMobileNavTop();
  };

  const syncMobileNavTop = () => {
    const header = document.querySelector('[data-site-header]');
    const top = Math.max(0, header?.getBoundingClientRect().bottom || 0);
    document.body.style.setProperty('--mobile-nav-top', `${top}px`);
  };

  const observeHeaderHeight = () => {
    const announcement = document.querySelector('[data-site-announcement]');
    const header = document.querySelector('[data-site-header]');

    syncHeaderHeight();
    window.addEventListener('scroll', syncMobileNavTop, { passive: true });
    window.addEventListener('resize', syncMobileNavTop, { passive: true });

    if (!('ResizeObserver' in window)) {
      window.addEventListener('resize', syncHeaderHeight, { passive: true });
      return;
    }

    const headerResizeObserver = new ResizeObserver(syncHeaderHeight);
    if (announcement) headerResizeObserver.observe(announcement);
    if (header) headerResizeObserver.observe(header);
  };

  const readHeaderData = wrapper => {
    const dataEl = wrapper?.querySelector('[data-section-data]');
    if (!dataEl) return {};

    try {
      return JSON.parse(dataEl.textContent);
    } catch (error) {
      return {};
    }
  };

  const firstSectionSupportsTransparentHeader = main => {
    const firstSection = main?.querySelector('.shopify-section');
    if (!firstSection) return false;

    return Boolean(firstSection.querySelector([
      '.image-with-text-overlay--wide',
      '.article-template--width-wide',
      '.blog-template--width-wide',
      '.slideshow--width-wide',
      '.video-section--width-wide-true',
      '.wide_display--true',
    ].join(',')));
  };

  const syncHeaderTransparency = () => {
    const wrapper = document.querySelector('.site-header__outer-wrapper');
    const main = document.querySelector('main');
    if (!wrapper || !main) return;

    const data = readHeaderData(wrapper);
    const mode = data.transparent_header || 'none';
    const pageSupportsMode = data.current_page === 'index' || mode === 'site';
    const supportsTransparency = mode !== 'none'
      && pageSupportsMode
      && firstSectionSupportsTransparentHeader(main);
    const transparentNow = supportsTransparency
      && (!data.sticky_header || document.documentElement.scrollTop < 150);

    main.classList.toggle('main--transparent-header', supportsTransparency);
    wrapper.classList.toggle('site-header__outer-wrapper--transparent', transparentNow);
    document.body.classList.toggle('banner-present--true', transparentNow);
  };

  let headerTransparencyFrame;
  const queueHeaderTransparencySync = () => {
    window.cancelAnimationFrame(headerTransparencyFrame);
    headerTransparencyFrame = window.requestAnimationFrame(syncHeaderTransparency);
  };

  const observeHeaderTransparency = () => {
    syncHeaderTransparency();
    window.addEventListener('scroll', queueHeaderTransparencySync, { passive: true });
    document.addEventListener('shopify:section:load', queueHeaderTransparencySync);
  };

  const observeCartCount = () => {
    const cartCount = document.querySelector('[data-header-cart-count]');
    if (!cartCount) return;

    const syncCartCount = () => {
      const itemCount = Number.parseInt(cartCount.textContent, 10) || 0;
      cartCount.classList.toggle('header-controls__cart-count--empty', itemCount === 0);
    };

    syncCartCount();
    new MutationObserver(syncCartCount).observe(cartCount, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  };

  const observeMobileMenuState = () => {
    const mobileNav = document.querySelector('[data-mobile-nav]');
    const trigger = document.querySelector('[data-mobile-nav-trigger]');
    const closeButton = mobileNav?.querySelector('[data-mobile-nav-close]');
    if (!mobileNav || !trigger) return;

    if (closeButton && trigger.dataset.sfgCloseBound !== 'true') {
      trigger.dataset.sfgCloseBound = 'true';
      trigger.addEventListener('click', event => {
        if (mobileNav.dataset.open !== 'true') return;

        event.preventDefault();
        event.stopImmediatePropagation();
        closeButton.click();
      }, true);
    }

    const syncMobileMenuState = () => {
      const isOpen = mobileNav.dataset.open === 'true';
      document.body.classList.toggle('mobile-menu-open', isOpen);
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      trigger.setAttribute(
        'aria-label',
        isOpen ? 'Close menu' : trigger.dataset.closedLabel || trigger.getAttribute('aria-label') || 'Menu',
      );
    };

    trigger.dataset.closedLabel ||= trigger.getAttribute('aria-label') || 'Menu';
    syncMobileMenuState();
    new MutationObserver(syncMobileMenuState).observe(mobileNav, {
      attributes: true,
      attributeFilter: ['data-open'],
    });
  };

  const closeMobileMenuAtDesktop = () => {
    if (window.innerWidth < 1200) return;

    document.querySelectorAll('[data-mobile-nav][data-open="true"]').forEach(mobileNav => {
      mobileNav.querySelector('[data-mobile-nav-close]')?.click();
    });
  };

  let mobileMenuResizeFrame;
  window.addEventListener('resize', () => {
    window.cancelAnimationFrame(mobileMenuResizeFrame);
    mobileMenuResizeFrame = window.requestAnimationFrame(closeMobileMenuAtDesktop);
  }, { passive: true });

  document.addEventListener('shopify:section:unload', event => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    document.querySelectorAll('[data-mobile-nav-section-id]').forEach(mobileNav => {
      if (mobileNav.dataset.mobileNavSectionId === sectionId) mobileNav.remove();
    });
  });

  document.addEventListener('shopify:section:load', event => {
    if (!(event.target instanceof Element) || !event.target.matches('.site-header__outer-wrapper')) return;

    syncHeaderHeight();
    observeMobileMenuState();
    closeMobileMenuAtDesktop();
  });

  const markCartItemUpdating = control => {
    const cartItem = control?.closest('.cart-item:not(.cart-item--titles), .cart-drawer__item');
    if (!cartItem) return;

    cartItem.classList.add('is-updating');
    cartItem.setAttribute('aria-busy', 'true');
    window.setTimeout(() => {
      if (!cartItem.isConnected) return;
      cartItem.classList.remove('is-updating');
      cartItem.removeAttribute('aria-busy');
    }, 10000);
  };

  // The cart page performs a native form submit and the drawer refreshes its
  // HTML after an AJAX quantity or removal change. Mark the complete product
  // card before either action begins so customers receive immediate feedback.
  document.addEventListener('click', event => {
    const control = event.target.closest(
      '[data-quantity-minus], [data-quantity-plus], [data-remove-item], .cart-item__remove',
    );
    if (control?.closest('.cart-section, [data-cart-drawer]')) markCartItemUpdating(control);
  }, true);
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-quantity-input]');
    if (input?.closest('.cart-section, [data-cart-drawer]')) markCartItemUpdating(input);
  }, true);

  // Committing a manually entered drawer quantity with Enter must use the
  // drawer's AJAX change handler instead of submitting the form to /cart.
  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || !(event.target instanceof Element)) return;

    const input = event.target.closest('[data-cart-drawer] [data-quantity-input]');
    if (!input) return;

    event.preventDefault();
    input.blur();
  });

  const discountStorageKey = 'sfg-cart-discount-code';
  const readStoredDiscount = () => {
    try {
      return window.sessionStorage.getItem(discountStorageKey) || '';
    } catch (error) {
      return '';
    }
  };
  const storeDiscount = value => {
    try {
      const discount = value.trim();
      if (discount) window.sessionStorage.setItem(discountStorageKey, discount);
      else window.sessionStorage.removeItem(discountStorageKey);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  };
  const restoreCartDiscount = () => {
    const input = document.querySelector('#cart-template-discount[data-sfg-discount-input]');
    const storedDiscount = readStoredDiscount();
    if (!input || !storedDiscount || input.value) return;

    input.value = storedDiscount;
    input.closest('details')?.setAttribute('open', '');
  };

  document.addEventListener('input', event => {
    const input = event.target.closest('[data-sfg-discount-input]');
    if (input?.id === 'cart-template-discount') storeDiscount(input.value);
  });
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-cart-drawer] form');
    if (!form) return;

    storeDiscount(form.querySelector('#cart-drawer-discount')?.value || '');
  }, true);
  restoreCartDiscount();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observeHeaderHeight();
      observeHeaderTransparency();
      observeCartCount();
      observeMobileMenuState();
      restoreCartDiscount();
    }, { once: true });
  } else {
    observeHeaderHeight();
    observeHeaderTransparency();
    observeCartCount();
    observeMobileMenuState();
  }
  document.addEventListener('submit', async event => {
    const recommendationForm = event.target.closest('[data-sfg-cart-recommendation]');
    const legacyProductForm = event.target.closest('form[action="/cart/add"]');

    if (!recommendationForm && !legacyProductForm) return;

    if (legacyProductForm && !recommendationForm) {
      if (legacyProductForm.querySelector('[data-product-atc]')) return;

      event.preventDefault();
      const button = event.submitter || legacyProductForm.querySelector('[name="add"]');
      button?.setAttribute('aria-busy', 'true');
      if (button) button.disabled = true;

      try {
        ensureRuntime();
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(legacyProductForm),
        });

        if (!response.ok) throw new Error('Unable to add product to cart');

        if (window.PXUTheme.cartDrawer?.loadDrawer) {
          window.PXUTheme.cartDrawer.loadDrawer(true);
        } else {
          window.location.assign('/cart');
        }
      } catch (error) {
        legacyProductForm.submit();
      } finally {
        window.setTimeout(() => {
          button?.removeAttribute('aria-busy');
          if (button) button.disabled = false;
        }, 1200);
      }
      return;
    }

    ensureRuntime();
    event.preventDefault();
    const button = recommendationForm.querySelector('button[type="submit"]');
    if (button) button.disabled = true;

    try {
      const response = await fetch(window.PXUTheme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(recommendationForm),
      });

      if (!response.ok) throw new Error('Unable to add recommendation to cart');

      document.querySelector('[data-close-search]')?.click();
      if (window.PXUTheme.cartDrawer?.loadDrawer) {
        window.PXUTheme.cartDrawer.loadDrawer(true);
      } else {
        window.location.assign(window.PXUTheme.routes.cart_url);
      }
    } catch (error) {
      recommendationForm.submit();
    } finally {
      if (button) button.disabled = false;
    }
  });
})();
